// Regex that is used to find and replace unwanted characters in a series id.
// Currently includes:
// - Newline characters, which trigger a known issue in MUI-x datagrid
//   https://github.com/mui/mui-x/issues/1953
// This regex is intentionally defined as a constant outside of the utility
// function that uses it as a performance optimization because this function
// may be called many times and there is a cost to re-creating the regex every
// time the function is called.
const SERIES_ID_REPLACE_REGEX = /[\r\n]+/g;

/**
 * Normalizes the raw series id to remove characters that may cause problems
 * when using it as a unique identifier.
 */
export function normalizeSeriesId(rawSeriesId: string): string {
  return rawSeriesId.replace(SERIES_ID_REPLACE_REGEX, '');
}

/**
 * Returns a function that should be used to generate series/datset ids for a given chart. This function guarantees
 * the following:
 * - The series id is normalized to remove characters that may cause issues.
 * - The series id is deduplicated across calls of the same series id generator function.
 */
export function getSeriesIdGenerator() {
  const seriesIdMap = new Map<string, number>();

  /**
   * Call this function to generate a unique id for a series or dataset.
   */
  function generateSeriesId(rawSeriesId: string, deduplicate = true) {
    // Series id based on the chart and name
    const baseSeriesId = normalizeSeriesId(rawSeriesId);

    if (!deduplicate) {
      return baseSeriesId;
    }

    // Series id we will save. For the first instance seen of a `baseSeriesId`, we use that as the
    // `seriesId`. If we see additional items with the same `baseSeriesId`, we add a counter to disambiguate them.
    // We previously used the dataset index, but this could lead to annoying behavior if the order of the results
    // changes when refreshing or adjusting the time window.
    let seriesId = baseSeriesId;
    const seriesIdCount = seriesIdMap.get(baseSeriesId) ?? 0;
    if (seriesIdCount) {
      // Only add the count to the series if we have already seen this `baseSeriesId`.
      // Use a delimiter (3 underscores) to avoid conflicts with other series names that end
      // with numbers.
      seriesId = `${seriesId}___${seriesIdCount}`;
    }

    // Increment count for series id.
    seriesIdMap.set(baseSeriesId, seriesIdCount + 1);

    return seriesId;
  }

  return generateSeriesId;
}
